import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Field } from '../components/Field';
import { Feedback } from '../components/Feedback';
import { apiPost } from '../lib/api';
import { PasswordInput } from '../components/PasswordInput';

/**
 * Route: `/dashboard/settings`. Shows org/user info and lets the user change
 * their password. API key management has moved to the dedicated Keys page.
 */
export function SettingsPage() {
  const { user } = useAuth();
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'warn'; msg: string } | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    setFeedback(null);
    if (newPassword.length < 8) {
      setFeedback({ type: 'error', msg: 'new password must be at least 8 characters' });
      return;
    }
    if (newPassword !== confirm) {
      setFeedback({ type: 'error', msg: 'passwords do not match' });
      return;
    }
    setBusy(true);
    try {
      await apiPost('/v1/auth/change-password', { currentPassword, newPassword });
      setFeedback({ type: 'success', msg: 'password updated.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
    } catch (err) {
      setFeedback({ type: 'error', msg: err instanceof Error ? err.message : 'change password failed' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="text-muted" style={{ marginBottom: 8 }}>── settings ─────────────────────────────────────</div>
      <div className="box" style={{ marginBottom: 16 }}>
        <Field label="organization" value={user?.orgName ?? '—'} />
        <Field label="email" value={user?.email ?? '—'} />
        <Field label="role" value={user?.role ?? '—'} />
        <Field label="org id" value={user?.orgId ?? '—'} />
      </div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── api keys ─────────────────────────────────────</div>
      <div className="box" style={{ marginBottom: 16 }}>
        <p style={{ marginBottom: 8 }}>
          api key management has moved to its own page. mint, rotate, scope, and revoke keys per standard.
        </p>
        <Link to="/dashboard/keys" className="btn btn-primary">→ manage api keys</Link>
      </div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── change password ──────────────────────────────</div>

      {feedback && <Feedback type={feedback.type} message={feedback.msg} />}

      <form onSubmit={changePassword} className="box">
        <div className="form-row">
          <label className="label">current password</label>
          <PasswordInput
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
        </div>
        <div className="form-row">
          <label className="label">new password <span className="text-muted">(min 8)</span></label>
          <PasswordInput
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <div className="form-row">
          <label className="label">confirm new password</label>
          <PasswordInput
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy || !currentPassword || !newPassword}>
          {busy ? 'updating…' : 'update password'}
        </button>
      </form>
    </div>
  );
}
